import React from 'react'
import { Layout, Header, Intro, ItemTitle } from '../../../../components'

import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/design/'}
        state="design"
        title={data.title}
      />
      <Layout
        slug={data.slug + '/design/'}
        site={data.site}
        state="design"
        title={data.title}
      >
        <Intro>
          <p>
            Use these arrangement classes to manipulate if and where an element
            appears within a layout.
          </p>
        </Intro>
        <ItemTitle>Display</ItemTitle>
        <p>
          Use these display classes to specify an element's rendering box.
          Display affects visibility as well as the page flow. The below display
          classes apply to all breakpoints. To specify display behavior at
          certain breakpoints, use the following format to add a breakpoint
          abbreviation{' '}
          <span class="inline__code">
            u-display-{'{'}breakpoint{'}'}--{'{'}value{'}'}
          </span>
          .
        </p>

        <table class="showcase-table l-margin-bottom--none">
          <tbody>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-display--block</span>
                </div>
              </td>
              <td className="table__description">
                Sets `display: block` to element, making it a block. It will
                start on a new line and take all available width.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-display--inline</span>
                </div>
              </td>
              <td className="table__description">
                Sets `display: inline` to element, making it inline. It will
                start in the same line as the previous element. Any specified
                height and width properties are negated.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-display--inline-block</span>
                </div>
              </td>
              <td className="table__description">
                Sets `display: inline-block` to element, making it an inline
                block. The element starts inline with the element before it but
                specified height and width values will take effect.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-display--none</span>
                </div>
              </td>
              <td className="table__description">
                Sets `display: none` to element, removing it from the page flow.
              </td>
            </tr>
          </tbody>
        </table>

        <ItemTitle className="l-margin-top--xxlarge">Visibility</ItemTitle>
        <p>
          Use visibility classes to show or hide an element while keeping them
          in the page flow. The below visibility classes apply to all
          breakpoints. To specify visibility at certain breakpoints, use the
          following format to add a breakpoint abbreviation{' '}
          <span class="inline__code">
            u-visible-{'{'}breakpoint{'}'}
          </span>
          .
        </p>
        <table class="showcase-table l-margin-bottom--none">
          <tbody>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-hidden</span>
                </div>
              </td>
              <td className="table__description">
                Sets `visibility: hidden` to element. Hidden items still take
                space on the page.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-visible</span>
                </div>
              </td>
              <td className="table__description">
                Sets `visibility: visible` to element, forcing it to appear on
                the page.
              </td>
            </tr>
          </tbody>
        </table>

        <ItemTitle className="l-margin-top--xxlarge">Screen Reader</ItemTitle>
        <p>
          Use the screen reader utility instead of visibility to hide an element
          but make it easily available for screen readers.
        </p>

        <table class="showcase-table l-margin-bottom--none">
          <tbody>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-screen-reader</span>
                </div>
              </td>
              <td className="table__description">
                Sets element as a screen reader helper, visually hiding it from
                the page while keeping it in the DOM for accessibility utility.
              </td>
            </tr>
          </tbody>
        </table>
        <ItemTitle className="l-margin-top--xxlarge">Position</ItemTitle>
        <p>
          Use the position classes to determine the positioning method used to
          place an element.
        </p>
        <table class="showcase-table l-margin-bottom--none">
          <tbody>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-position--absolute</span>
                </div>
              </td>
              <td className="table__description">
                Sets `position: absolute` to element.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-position--fixed</span>
                </div>
              </td>
              <td className="table__description">
                Sets `position: fixed` to element.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-position--relative</span>
                </div>
              </td>
              <td className="table__description">
                Sets `position: relative` to element.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-position--unset</span>
                </div>
              </td>
              <td className="table__description">
                Sets `position: unset` to element, allowing the element to take
                either an inherited value or initial value depending on its
                parent.
              </td>
            </tr>
          </tbody>
        </table>
        <ItemTitle className="l-margin-top--xxlarge">Pull</ItemTitle>
        <p>
          Pull utilities will set the float property to align an object as
          needed and let elements flow around it.
        </p>
        <table class="showcase-table l-margin-bottom--none">
          <tbody>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-pull--left</span>
                </div>
              </td>
              <td className="table__description">
                Sets `float: left` to element.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-pull--right</span>
                </div>
              </td>
              <td className="table__description">
                Sets `float: right` to element.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-pull--center</span>
                </div>
              </td>
              <td className="table__description">
                Sets `float: center` to element.
              </td>
            </tr>
            <tr>
              <td>
                <div class="u-flex-container u-flex-align--middle">
                  <span class="table__code">u-pull--none</span>
                </div>
              </td>
              <td className="table__description">
                Sets `float: none` to element.
              </td>
            </tr>
          </tbody>
        </table>
      </Layout>
    </>
  )
}
